import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Package, Search, RefreshCw, ChevronLeft, ChevronRight,
  Globe, Star, Building2, Filter, Users, TrendingUp, Loader2,
  ChevronDown, CheckCircle2, XCircle, Clock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 20;

const STATUS_CONFIG = {
  active:   { color: 'bg-emerald-100 text-emerald-800', label: 'Aktif', icon: CheckCircle2 },
  inactive: { color: 'bg-gray-100 text-gray-600',       label: 'Pasif', icon: XCircle },
  pending:  { color: 'bg-yellow-100 text-yellow-800',   label: 'Bekliyor', icon: Clock },
};

const PLAN_COLORS = {
  free:     'bg-gray-100 text-gray-600',
  premium:  'bg-amber-100 text-amber-700',
  featured: 'bg-purple-100 text-purple-700',
  basic:    'bg-blue-100 text-blue-700',
};

const UltraSuppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState({ total: 0, active: 0, featured: 0, catalogue: 0 });

  const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await axios.get(`${API}/ultra/suppliers?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuppliers(res.data.items || []);
      setTotal(res.data.total || 0);

      // Compute KPIs from current page (full stats would need a separate endpoint)
      const all = res.data.items || [];
      setKpis(prev => ({
        total: res.data.total || 0,
        active: all.filter(s => s.status === 'active').length,
        featured: all.filter(s => s.plan === 'featured').length,
        catalogue: all.reduce((sum, s) => sum + (s.catalogue_items || 0), 0),
      }));
    } catch (e) {
      setError('Tedarikçiler yüklenemedi. Lütfen tekrar deneyin.');
      console.error('Suppliers fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const kpiCards = [
    { label: 'Toplam Tedarikçi', value: kpis.total, icon: Building2, color: 'bg-purple-50 text-purple-600' },
    { label: 'Aktif Tedarikçi', value: kpis.active, icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-600' },
    { label: 'Öne Çıkan Plan', value: kpis.featured, icon: Star, color: 'bg-amber-50 text-amber-600' },
    { label: 'Katalog Öğesi', value: kpis.catalogue, icon: Package, color: 'bg-blue-50 text-blue-600' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tedarikçi Yönetimi</h1>
          <p className="text-sm text-gray-500 mt-0.5">Platformdaki tüm tedarikçi hesapları</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchSuppliers} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Yenile
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((k, i) => {
          const Icon = k.icon;
          return (
            <Card key={i} className="border-0 shadow-sm" data-testid={`kpi-supplier-${i}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{k.label}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{k.value}</p>
                  </div>
                  <div className={`p-2.5 rounded-xl ${k.color}`}><Icon className="h-5 w-5" /></div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="İsim, ülke, e-posta ara..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="pl-9 bg-gray-50 border-gray-200"
                data-testid="supplier-search-input"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              data-testid="supplier-status-filter"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
              <option value="pending">Bekliyor</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm flex items-center gap-2">
          <XCircle className="h-4 w-4 shrink-0" />{error}
        </div>
      )}

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-purple-400" />
            </div>
          ) : suppliers.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Package className="h-12 w-12 mx-auto mb-3 text-gray-200" />
              <p className="font-medium">Tedarikçi bulunamadı</p>
              <p className="text-sm mt-1">Arama kriterlerinizi değiştirin</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="suppliers-table">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-5 py-3.5">Tedarikçi</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3.5">Ülke</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3.5">Plan</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3.5">Katalog</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3.5">Durum</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3.5">Skor</th>
                    <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3.5">Kayıt Tarihi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {suppliers.map(s => {
                    const statusCfg = STATUS_CONFIG[s.status] || STATUS_CONFIG.active;
                    const StatusIcon = statusCfg.icon;
                    return (
                      <tr key={s.id} className="hover:bg-gray-50/50 transition-colors" data-testid={`supplier-row-${s.id}`}>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                              <Package className="h-4 w-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{s.name || '—'}</p>
                              <p className="text-xs text-gray-400">{s.id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-600 flex items-center gap-1.5">
                            <Globe className="h-3.5 w-3.5 text-gray-400" />{s.country || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${PLAN_COLORS[s.plan] || PLAN_COLORS.free}`}>
                            {s.plan || 'free'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm font-semibold text-gray-700">{s.catalogue_items}</span>
                          <span className="text-xs text-gray-400 ml-1">öğe</span>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>
                            <StatusIcon className="h-3 w-3" />{statusCfg.label}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-100 rounded-full h-1.5 w-16">
                              <div
                                className={`h-1.5 rounded-full ${s.score >= 70 ? 'bg-emerald-500' : s.score >= 40 ? 'bg-amber-400' : 'bg-gray-300'}`}
                                style={{ width: `${s.score}%` }}
                              />
                            </div>
                            <span className={`text-xs font-bold ${s.score >= 70 ? 'text-emerald-600' : s.score >= 40 ? 'text-amber-600' : 'text-gray-400'}`}>
                              {s.score}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-xs text-gray-400">
                            {s.created_at ? new Date(s.created_at).toLocaleDateString('tr-TR') : '—'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">{total} tedarikçiden {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} gösteriliyor</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || loading}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-gray-600">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages || loading}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltraSuppliers;
